import { ArrowBendLeftUp } from "@phosphor-icons/react";
import type { MouseEvent, ReactNode } from "react";
import {
  buildAppUrl,
  displayAppUrl,
  navigateToCreate,
  navigateToRoom,
} from "../config";
import { YayLogo } from "./YayLogo";

function YayWord() {
  return <YayLogo variant="white" className="yay-word" alt="Yay" />;
}

type HomeLinkAction = "lobby" | "create" | { room: string };

function homeLinkHref(action: HomeLinkAction): string {
  if (action === "lobby") return buildAppUrl();
  if (action === "create") return buildAppUrl({ create: true });
  return buildAppUrl({ room: action.room });
}

function homeLinkLabel(action: HomeLinkAction): string {
  return displayAppUrl(
    action === "lobby"
      ? undefined
      : action === "create"
        ? { create: true }
        : { room: action.room },
  );
}

function onHomeLinkClick(
  event: MouseEvent<HTMLAnchorElement>,
  action: HomeLinkAction,
): void {
  event.preventDefault();
  if (action === "lobby") {
    window.location.assign(homeLinkHref("lobby"));
    return;
  }
  if (action === "create") {
    navigateToCreate();
    return;
  }
  navigateToRoom(action.room);
}

function HomeLink({
  action,
  children,
  className,
}: {
  action: HomeLinkAction;
  children?: ReactNode;
  className?: string;
}) {
  return (
    <a
      href={homeLinkHref(action)}
      className={className ? `home-link ${className}` : "home-link"}
      onClick={(event) => onHomeLinkClick(event, action)}
    >
      {children ?? homeLinkLabel(action)}
    </a>
  );
}

export function HomePage() {
  return (
    <div className="home-page">
      <main className="home-content">
        <header className="home-header">
          <YayLogo variant="purple" className="home-logo" alt="Yay" />
        </header>

        <p>
          <YayWord /> is watch-together for friends. No account needed — pick
          a username and you&apos;re in. The global lobby lives at{" "}
          <HomeLink action="lobby" /> — everyone watches the same stream. Want
          a private room? Open <HomeLink action="create" /> and share the
          link; anyone with the URL joins the same room. Already have a code?
          Join at <span className="home-code">/?room=your-code</span> — swap
          in the code from your shared link.
        </p>

        <p>
          In a private room, paste a YouTube link in the top bar to watch
          together. Hit <span className="home-nerd">nerd</span> for flashcards
          while you study. Chat, avatars, and presence are always on.{" "}
          <span className="home-enter-wrap">
            <HomeLink action="create" className="home-enter-link">
              Enter <YayWord />
            </HomeLink>
            <span className="home-enter-callout" aria-hidden>
              <ArrowBendLeftUp size={32} color="#9147FF" />
              <span className="home-try-it">try it out!</span>
            </span>
          </span>
        </p>
      </main>

      <footer className="home-footer">
        <a href="/terms">terms</a>
        <a href="/privacy">privacy</a>
        <a href="/refund">refunds</a>
        <a href="/guidelines">guidelines</a>
        <a href="/dmca">copyright</a>
        <a href="/contact">contact</a>
      </footer>
    </div>
  );
}
